<script setup lang="ts">
/**
 * Picks one image with a preview — a contact photo, an avatar, a letterhead
 * logo. Click the frame or the button to choose, drop a file onto the frame,
 * replace or remove it. With `downscale` the pick is scaled and re-encoded in
 * the browser first (EXIF orientation applied and stripped, see
 * `downscaleImage`), so a 12-megapixel phone photo arrives as a sane master.
 *
 * `v-model` is a `File`/`Blob` after a pick, or a URL string for the stored
 * image; removing sets `null`. A file of the wrong type or over `maxBytes` is
 * refused here with an error under the frame and never reaches `v-model`;
 * `errorText` shows the caller's own (upload) error.
 *
 * The `placeholder` slot replaces the empty frame's content (initials).
 */
import ImagePlus from 'lucide-vue-next/dist/esm/icons/image-plus.js'; import Trash2 from 'lucide-vue-next/dist/esm/icons/trash-2.js';
import { computed, onBeforeUnmount, ref, toRef, useId, watch } from 'vue';
import { formatBytes } from '../Attachment/fileKind';
import { NxButton } from '../Button';
import { NxIconButton } from '../IconButton';
import { useFileDrop } from '../FileDrop/useFileDrop';
import { downscaleImage, type NxDownscaleOptions } from './downscale';

export type NxImageInputValue = File | Blob | string | null;

const props = withDefaults(
  defineProps<{
    /** Frame shape: a round avatar, a square logo or a 4:5 portrait. */
    shape?: 'round' | 'square' | 'portrait';
    size?: 'sm' | 'md' | 'lg';
    /** Buttons under the frame (`stack`) or beside it (`inline`). */
    layout?: 'stack' | 'inline';
    /** File types the picker offers and the drop accepts. */
    accept?: string;
    /** Scale and re-encode the pick first; `true` uses the defaults (1200 px JPEG). */
    downscale?: boolean | NxDownscaleOptions;
    /** Largest file (after downscaling) in bytes. */
    maxBytes?: number;
    /** Accessible description of the image, e.g. „Foto von Anna Berg“. */
    alt?: string;
    /** Text in the empty frame (initials); the `placeholder` slot overrides it. */
    initials?: string;
    chooseLabel?: string;
    replaceLabel?: string;
    removeLabel?: string;
    dropLabel?: string;
    /** Local refusal of a file that is no image of an accepted type. */
    typeErrorText?: string;
    /** The caller's own error (upload failed); a local refusal wins while set. */
    errorText?: string | null;
    disabled?: boolean;
    /** Shows the buttons disabled with a spinner-free busy state (upload running). */
    busy?: boolean;
    /** Why the buttons are disabled, as their tooltip. */
    disabledHint?: string;
    locale?: string;
  }>(),
  {
    shape: 'round',
    size: 'md',
    layout: 'stack',
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    downscale: false,
    maxBytes: undefined,
    alt: 'Bild',
    initials: '',
    chooseLabel: 'Bild auswählen',
    replaceLabel: 'Ersetzen',
    removeLabel: 'Bild entfernen',
    dropLabel: 'Bild hier ablegen',
    typeErrorText: 'Diese Datei ist kein unterstütztes Bild.',
    errorText: null,
    disabled: false,
    busy: false,
    disabledHint: undefined,
    locale: undefined,
  }
);

const emit = defineEmits<{
  /** A new image was picked (after downscaling). */
  change: [file: File | Blob];
  remove: [];
}>();

const model = defineModel<NxImageInputValue>({ default: null });

const inputRef = ref<HTMLInputElement | null>(null);
const localError = ref<string | null>(null);
const processing = ref(false);
const broken = ref(false);
const errorId = useId();

// Preview URL: strings as given, blobs through an object URL that is revoked
// as soon as the value changes or the component goes.
const objectUrl = ref<string | null>(null);
function releaseUrl() {
  if (objectUrl.value) URL.revokeObjectURL(objectUrl.value);
  objectUrl.value = null;
}
/**
 * Bumped by every new pick and every outside change of the value, so a
 * downscale that finishes late cannot overwrite a newer value or a reset form.
 */
let generation = 0;
watch(
  model,
  (value) => {
    generation += 1;
    releaseUrl();
    broken.value = false;
    if (value && typeof value !== 'string') objectUrl.value = URL.createObjectURL(value);
  },
  { immediate: true }
);
onBeforeUnmount(releaseUrl);

const previewUrl = computed(() =>
  typeof model.value === 'string' ? model.value : (objectUrl.value ?? '')
);
const hasImage = computed(() => previewUrl.value !== '' && !broken.value);
const inactive = computed(() => props.disabled || props.busy || processing.value);
const shownError = computed(() => localError.value ?? props.errorText ?? null);

/** Whether a file matches an `accept` list of MIME types, `type/*` and `.ext`. */
function accepts(file: File): boolean {
  const rules = props.accept
    .split(',')
    .map((r) => r.trim().toLowerCase())
    .filter(Boolean);
  if (!rules.length) return file.type.startsWith('image/');
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return rules.some((rule) =>
    rule.startsWith('.')
      ? name.endsWith(rule)
      : rule.endsWith('/*')
        ? type.startsWith(rule.slice(0, -1))
        : type === rule
  );
}

async function take(file: File | undefined) {
  if (!file || inactive.value) return;
  localError.value = null;
  if (!accepts(file)) {
    localError.value = props.typeErrorText;
    return;
  }
  let result: File | Blob = file;
  if (props.downscale) {
    const ticket = ++generation;
    processing.value = true;
    try {
      result = await downscaleImage(file, props.downscale === true ? {} : props.downscale);
    } catch {
      if (ticket === generation) localError.value = props.typeErrorText;
      return;
    } finally {
      processing.value = false;
    }
    if (ticket !== generation) return;
  }
  if (props.maxBytes != null && result.size > props.maxBytes) {
    localError.value = `Das Bild ist zu groß (höchstens ${formatBytes(props.maxBytes, props.locale)}).`;
    return;
  }
  model.value = result;
  emit('change', result);
}

function choose() {
  if (inactive.value) return;
  inputRef.value?.click();
}

function onInputChange(event: Event) {
  const input = event.target as HTMLInputElement;
  void take(input.files?.[0]);
  // Reset so picking the same file again still fires `change`.
  input.value = '';
}

function remove() {
  if (inactive.value) return;
  localError.value = null;
  model.value = null;
  emit('remove');
}

const { isDragActive, handlers } = useFileDrop({
  onDrop: (files) => void take(files[0]),
  disabled: toRef(() => inactive.value),
});

const frameLabel = computed(() =>
  hasImage.value ? `${props.alt} – ${props.replaceLabel}` : `${props.alt} – ${props.chooseLabel}`
);
</script>

<template>
  <div
    class="nx-image-input"
    :class="[
      `is-${shape}`,
      `is-${size}`,
      `is-${layout}`,
      { 'is-disabled': disabled, 'is-busy': busy || processing },
    ]"
  >
    <button
      type="button"
      class="nx-image-input__frame"
      :class="{ 'is-drag': isDragActive, 'has-image': hasImage }"
      :aria-label="frameLabel"
      :aria-describedby="shownError ? errorId : undefined"
      :disabled="inactive"
      :title="inactive ? disabledHint : undefined"
      data-testid="nx-image-input-frame"
      @click="choose"
      v-on="handlers"
    >
      <img
        v-if="hasImage"
        class="nx-image-input__img"
        :src="previewUrl"
        alt=""
        @error="broken = true"
      />
      <span v-else class="nx-image-input__placeholder" aria-hidden="true">
        <slot name="placeholder">
          <span v-if="initials" class="nx-image-input__initials">{{ initials }}</span>
          <ImagePlus v-else class="nxt-icon-lg" />
        </slot>
      </span>
      <span v-if="isDragActive" class="nx-image-input__drop" aria-hidden="true">
        {{ dropLabel }}
      </span>
    </button>

    <div class="nx-image-input__side">
      <div class="nx-image-input__actions">
        <NxButton
          variant="outline"
          height="xs"
          :disabled="inactive"
          :title="inactive ? disabledHint : undefined"
          data-testid="nx-image-input-choose"
          @click="choose"
        >
          {{ hasImage ? replaceLabel : chooseLabel }}
        </NxButton>
        <NxIconButton
          v-if="model"
          danger
          height="xs"
          :title="inactive && disabledHint ? disabledHint : removeLabel"
          :aria-label="removeLabel"
          :disabled="inactive"
          data-testid="nx-image-input-remove"
          @click="remove"
        >
          <Trash2 class="nxt-icon-sm" aria-hidden="true" />
        </NxIconButton>
      </div>
      <p v-if="shownError" :id="errorId" class="nx-image-input__error" role="alert">
        {{ shownError }}
      </p>
    </div>

    <input
      ref="inputRef"
      class="nx-image-input__file"
      type="file"
      tabindex="-1"
      aria-hidden="true"
      :accept="accept"
      data-testid="nx-image-input-file"
      @change="onInputChange"
    />
  </div>
</template>

<style scoped>
.nx-image-input {
  --image-input-size: var(--nxt-avatar-size-xl);

  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-xs);
  max-width: 100%;

  &.is-sm {
    --image-input-size: var(--nxt-avatar-size-lg);
  }

  &.is-lg {
    --image-input-size: calc(var(--nxt-avatar-size-xl) * 1.75);
  }

  &.is-inline {
    flex-direction: row;
    align-items: center;
    gap: var(--nxt-space-md);
  }
}

.nx-image-input__frame {
  position: relative;
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: var(--image-input-size);
  height: var(--image-input-size);
  padding: 0;
  overflow: hidden;
  font: inherit;
  color: var(--nxt-fg-subtle);
  background: var(--nxt-surface-sunken-bg);
  border: var(--nxt-hairline) dashed var(--nxt-border-strong);
  border-radius: var(--nxt-radius-md);
  cursor: pointer;
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);

  .is-round & {
    border-radius: var(--nxt-radius-full);
  }

  .is-portrait & {
    height: calc(var(--image-input-size) * 1.25);
  }

  &.has-image {
    border-style: solid;
    border-color: var(--nxt-border);
  }

  &:hover:not(:disabled) {
    color: var(--nxt-fg-muted);
    border-color: var(--nxt-brand-border-hover);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
  }

  &.is-drag {
    border-style: dashed;
    border-color: var(--nxt-brand-border);
    background: var(--nxt-brand-subtle-bg);
  }

  &:disabled {
    cursor: default;
  }

  .is-busy & {
    opacity: 0.6;
  }
}

.nx-image-input__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;

  .is-square & {
    object-fit: contain;
  }
}

.nx-image-input__placeholder {
  display: grid;
  place-items: center;
}

.nx-image-input__initials {
  font-size: calc(var(--image-input-size) * 0.32);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: 1;
  color: var(--nxt-fg-subtle);
  user-select: none;
}

.nx-image-input__drop {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  line-height: var(--nxt-line-height-tight);
  text-align: center;
  color: var(--nxt-brand-subtle-fg);
  background: var(--nxt-brand-subtle-bg);
}

.nx-image-input__side {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;

  .is-inline & {
    align-items: flex-start;
  }
}

.nx-image-input__actions {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
}

.nx-image-input__error {
  max-width: var(--nxt-panel-width-xs);
  margin: 0;
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-danger-subtle-fg);
  text-align: center;

  .is-inline & {
    text-align: left;
  }
}

.nx-image-input__file {
  display: none;
}

@media (prefers-reduced-motion: reduce) {
  .nx-image-input__frame {
    transition: none;
  }
}
</style>
